// ─── src/NewsletterView.tsx ───────────────────────────────────────────────────
import { useState, useCallback } from "react";
import { Download, Mail, LayoutList, Search, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useNewsletter, useDeleteSubscriber, newsletterKeys } from "./hooks/useNewsletter";
import { NewsletterTable } from "./components/dashboard/NewsletterTable";
import { Pagination }      from "./components/dashboard/Pagination";
import { RegistrationsPerDayChart } from "./components/dashboard/RegistrationsPerDayChart";
import { TableSkeleton }   from "./components/ui/Skeleton";
import { StatCard }        from "./components/ui/StatCard";
import type { NewsletterSubscriber } from "./types/newsletter";

const LIMIT = 20;
// Loads every subscriber for the stat cards, chart and CSV export. The API
// has no page-size cap; 2000 used to silently drop anyone past that.
const ALL_LIMIT = 10_000;

function exportSubscribersToCsv(rows: NewsletterSubscriber[]) {
  const escape = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const header = "Name,Email,Source,Subscribed At";
  const body   = rows.map((r) =>
    [r.name, r.email, r.source ?? "", r.createdAt].map(escape).join(",")
  );
  const csv  = [header, ...body].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `newsletter_subscribers_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function NewsletterView() {
  const qc = useQueryClient();

  const [search,     setSearch]     = useState("");
  const [visualPage, setVisualPage] = useState(1);

  // ── Stats query: all subscribers (unfiltered) — for stat cards, chart and CSV export
  const { data: statsData } = useNewsletter({ page: 1, limit: ALL_LIMIT });
  const allRows     = statsData?.data ?? [];
  const totalCount  = statsData?.meta.total ?? allRows.length;
  const footerCount = allRows.filter((r) => r.source === "footer").length;
  const headerCount = allRows.filter((r) => r.source === "header-modal").length;

  // ── Table query: paginated + server-side search
  const { data: tableData, isLoading, isFetching, isError } = useNewsletter({
    page:  visualPage,
    limit: LIMIT,
    ...(search ? { search } : {}),
  });

  const { mutate: deleteSubscriber, isPending: isDeleting } = useDeleteSubscriber();

  const pageRows  = tableData?.data ?? [];
  const tableMeta = tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };

  const handleSearch = useCallback((v: string) => {
    setSearch(v);
    setVisualPage(1);
  }, []);

  const handleRefresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: newsletterKeys.all });
  }, [qc]);

  return (
    <div className="space-y-6">

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total Subscribers"  value={totalCount}  icon={Mail}       accent="bg-brand-teal" />
        <StatCard label="From Footer"        value={footerCount} icon={LayoutList} accent="bg-brand-orange" />
        <StatCard label="From Header Modal"  value={headerCount} icon={Mail}       accent="bg-brand-teal" />
      </div>

      {/* Signups per day */}
      <RegistrationsPerDayChart rows={statsData?.data} title="Subscribers per day" />

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-9 pr-8 py-2 text-sm bg-brand-card border border-brand-border
                       rounded-lg text-white placeholder-brand-muted
                       focus:outline-none focus:border-brand-orange transition-colors"
          />
          {search && (
            <button
              onClick={() => handleSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 ml-auto">
          {/* Refresh */}
          <button
            onClick={handleRefresh}
            disabled={isFetching}
            className="btn-ghost text-sm flex items-center gap-1.5"
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>

          {/* CSV Export — exports the full unfiltered list from stats query */}
          <button
            onClick={() => exportSubscribersToCsv(allRows)}
            disabled={allRows.length === 0}
            className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50"
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="card p-8 text-center text-red-400">
          Failed to load subscribers. Check your API key or network.
        </div>
      ) : (
        <>
          <NewsletterTable
            rows={pageRows}
            onDelete={(id) => deleteSubscriber(id)}
            isDeleting={isDeleting}
          />
          <Pagination
            page={tableMeta.page}
            totalPages={tableMeta.totalPages}
            total={tableMeta.total}
            limit={LIMIT}
            onPageChange={setVisualPage}
            isFetching={isFetching}
          />
        </>
      )}
    </div>
  );
}
